import { InfoPopover, RiCompass4Line, Stack } from 'component-library';
import { useTranslation } from 'react-i18next';

import { ChartHeading } from '@/components/Charts/components/ChartHeading';
import { usePathSet } from '@/hooks/usePathSet';
import { useProjectId } from '@/pages/shared/hooks/useProjectId';
import { FactCapsule } from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/FactCapsule';
import {
  LegendScale,
  SpeciesOccupancyLegend,
} from '@/pages/shared/projects/pages/project/pages/conservation/shared/components/LegendScale';
import { useFact } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFact';
import { useLegendRange } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useLegendRange';
import { useProject } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useProject';
import { isNullish } from '@/utils/booleans';
import { buildPath } from '@/utils/buildPath';

import { LandMonitoringCard } from './LandMonitoringCard';

export const IndicatorSpeciesCard = () => {
  const { t } = useTranslation();
  const pathSet = usePathSet();
  const projectId = useProjectId();
  const project = useProject();
  const floraAndFaunaMapImage = project?.images.find((image) => image.name === 'flora_and_fauna')?.url;
  const floraAndFaunaProjectAverage = useFact('r1_conservation_indicator_species_score');
  const speciesOccupancyProjectAverage = useFact('r1_conservation_species_occupancy_score');
  const legendRange = useLegendRange('r1_conservation_indicator_species_data_range');

  const hasSpeciesOccupancy = !isNullish(speciesOccupancyProjectAverage?.value);

  if (!hasSpeciesOccupancy && isNullish(floraAndFaunaProjectAverage?.value)) {
    return null;
  }

  return (
    <LandMonitoringCard
      data-testid='flora-and-fauna'
      title={t('shared.ncaDetail.details.landMonitoring.floraAndFauna.title')}
      heading={{
        heading: (
          <ChartHeading
            heading={t('shared.ncaDetail.details.landMonitoring.floraAndFauna.heading')}
            popover={
              <InfoPopover
                title={t('shared.ncaDetail.details.landMonitoring.floraAndFauna.explainers.indicatorSpecies.title')}
                body={t('shared.ncaDetail.details.landMonitoring.floraAndFauna.explainers.indicatorSpecies.body')}
                popoverTriggerProps={{
                  className: 'text-text-secondary',
                }}
              />
            }
          />
        ),
      }}
      image={
        floraAndFaunaMapImage
          ? {
              url: floraAndFaunaMapImage,
              alt: t('shared.ncaDetail.details.landMonitoring.floraAndFauna.mapImageAlt'),
            }
          : undefined
      }
      detailsPagePath={buildPath(pathSet.indicatorSpecies, { pathParams: { projectId } })}
      stats={
        <Stack spacing={6}>
          <Stack spacing={10} className='py-3'>
            <>
              {floraAndFaunaProjectAverage?.value && (
                <FactCapsule
                  label={t('shared.ncaDetail.details.landMonitoring.floraAndFauna.projectAverageValue')}
                  capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
                  fact={floraAndFaunaProjectAverage}
                />
              )}
              {hasSpeciesOccupancy && (
                <FactCapsule
                  label={t('shared.ncaDetail.details.landMonitoring.floraAndFauna.projectAverageValue')}
                  capsuleIconProps={{ icon: <RiCompass4Line />, variant: 'default' }}
                  fact={speciesOccupancyProjectAverage}
                />
              )}
            </>
          </Stack>
          <Stack spacing={2} className='p-2'>
            {hasSpeciesOccupancy ? (
              <SpeciesOccupancyLegend />
            ) : (
              <>
                <div className='h-3 w-full max-w-[222px] rounded-sm bg-gradient-to-r from-[#E8E7F3] to-[#595A90]' />
                <Stack direction='row' className='justify-between'>
                  <LegendScale customLegendRange={legendRange} />
                </Stack>
              </>
            )}
          </Stack>
        </Stack>
      }
    />
  );
};
